/**
 * Marqueurs superposés à une série temporelle.
 *
 * Un seul hook pour les cinq graphiques datés du dashboard : il charge les
 * marqueurs, retient ceux qui tombent dans la série affichée, calcule leur
 * effet sur CETTE métrique, et rend les traits à poser dans le graphique.
 *
 * ── Pourquoi un hook et pas un composant ────────────────────────────────────
 * Recharts classe ses enfants par type pour savoir quoi dessiner. Un composant
 * maison qui renverrait des `<ReferenceLine>` s'interposerait dans cette
 * classification. Le hook, lui, rend un TABLEAU d'éléments que le graphique
 * étale parmi ses enfants directs : Recharts les voit exactement comme s'ils
 * avaient été écrits à la main.
 *
 * Ces traits s'étalent AVANT la série, jamais après : Recharts 3 empile ses
 * marques dans l'ordre des enfants (le `isFront` de la version 2 a disparu),
 * et une annotation posée après passerait par-dessus la courbe qu'elle
 * commente.
 *
 * ── La période vient de la série, pas de l'URL ──────────────────────────────
 * Les bornes sont lues sur le premier et le dernier point fournis. Un marqueur
 * n'apparaît donc que si la courbe couvre vraiment sa date — et le calcul
 * d'impact ne dispose jamais que de ce qui est tracé, ce qui est la seule
 * façon honnête de le faire : le lecteur peut vérifier le verdict à l'œil sur
 * le graphique qu'il a sous les yeux.
 */

import { markerShownIn } from '@shared/types/stats';
import { useMemo, type ReactElement } from 'react';
import { ReferenceLine } from 'recharts';

import {
  computeImpact,
  formatImpactDelta,
  formatImpactWindows,
  impactTone,
  markersInPeriod,
  resolveMarkerX,
  seriesFrom,
  verdictLabel,
  type ImpactMode,
  type ImpactTone,
  type ImpactUnit,
  type MarkerImpact,
} from '../lib/markers';
import { formatDayShort } from '../lib/format';
import { IMPACT_TONE_COLORS, MARKER_LINE } from '../lib/theme';
import type { Marker } from '../types';
import { useMarkers } from './useMarkers';

/**
 * Au-delà de ce nombre de marqueurs visibles, les traits ne portent plus que
 * leur numéro.
 *
 * Quatre dates courtes sur une largeur de carte se lisent ; huit se
 * chevauchent et ne se lisent plus du tout. Passé le seuil, la date reste
 * accessible dans la bande sous le graphique, où elle a la place de s'écrire.
 */
const MAX_DATED_LABELS = 4;

interface MarkerLabelProps {
  /** Géométrie du trait, fournie par Recharts. */
  viewBox?: { x?: number; y?: number; width?: number; height?: number };
  text: string;
  color: string;
  /** Texte à GAUCHE du trait — pour un marqueur proche du bord droit. */
  anchorLeft: boolean;
}

/**
 * Étiquette d'un trait de marqueur.
 *
 * Étiquette maison et non `label={{ position: 'top' }}` de Recharts, pour deux
 * raisons que la version intégrée ne sait pas traiter :
 *
 *  · `position: 'top'` dessine AU-DESSUS de l'aire de tracé. Les marges hautes
 *    des graphiques valent 4 px : une étiquette de 11 px y est rognée par le
 *    SVG. Ici le texte est posé à l'intérieur, 14 px sous le haut de l'aire —
 *    visible quelle que soit la marge.
 *
 *  · `position: 'top'` centre le texte sur le trait. Sur un marqueur daté du
 *    dernier jour de la période, la moitié de la date sort du cadre. D'où
 *    `anchorLeft` : le texte se range du côté où il reste de la place.
 */
function MarkerLineLabel({ viewBox, text, color, anchorLeft }: MarkerLabelProps) {
  const x = viewBox?.x ?? 0;
  const y = viewBox?.y ?? 0;

  return (
    <text
      x={anchorLeft ? x - 5 : x + 5}
      y={y + 14}
      fill={color}
      fontSize={11}
      fontWeight={600}
      textAnchor={anchorLeft ? 'end' : 'start'}
    >
      {text}
    </text>
  );
}

export interface ChartMarkerConfig<T> {
  /** Valeur de la métrique pour un point. `null` = jour non mesuré. */
  value: (row: T) => number | null;
  /** `level` pour un flux, `growth` pour un cumul. Cf. `lib/markers.ts`. */
  mode: ImpactMode;
  unit: ImpactUnit;
  /**
   * La métrique est-elle meilleure quand elle monte ?
   *
   * Explicite et sans valeur par défaut : c'est la seule chose qui transforme
   * une direction en jugement, et la laisser implicite ferait afficher « vert »
   * sur la hausse d'un taux d'erreur.
   */
  higherIsBetter: boolean;
  /**
   * Famille du graphique : seuls les marqueurs de cette famille (ou « les
   * deux ») s'y affichent.
   */
  scope: 'analytics' | 'sales';
}

export interface ChartMarkerItem {
  marker: Marker;
  /** Numéro affiché sur le trait et dans la bande, à partir de 1. */
  position: number;
  impact: MarkerImpact;
  tone: ImpactTone;
  /** Écart avant/après déjà mis en forme. */
  delta: string;
  /** Détail des deux fenêtres, pour le `title` de la puce. */
  detail: string;
  verdict: string;
}

export interface ChartMarkers {
  items: ChartMarkerItem[];
  /** À étaler parmi les enfants du graphique Recharts. */
  lines: ReactElement[];
}

export function useChartMarkers<T extends { date: string }>(
  rows: readonly T[],
  config: ChartMarkerConfig<T>,
): ChartMarkers {
  const { data: markers } = useMarkers();
  const { value, mode, unit, higherIsBetter, scope } = config;

  return useMemo<ChartMarkers>(() => {
    const first = rows[0];
    const last = rows[rows.length - 1];
    if (!first || !last || !markers || markers.length === 0) {
      return { items: [], lines: [] };
    }

    const series = seriesFrom(rows, value);
    const visible = markersInPeriod(
      markers.filter((marker) => markerShownIn(marker, scope)),
      first.date,
      last.date,
    );

    const items: ChartMarkerItem[] = [];
    const lines: ReactElement[] = [];

    for (const marker of visible) {
      const x = resolveMarkerX(series, marker.date);
      // Impossible en principe — `markersInPeriod` a deja borne la date — mais
      // une serie qui sauterait tous les jours suivants le rendrait vrai.
      if (x === null) continue;

      const impact = computeImpact(series, marker.date, mode);
      const tone = impactTone(impact.verdict, higherIsBetter);
      const position = items.length + 1;

      items.push({
        marker,
        position,
        impact,
        tone,
        delta: formatImpactDelta(impact, unit),
        detail: formatImpactWindows(impact, unit),
        verdict: verdictLabel(impact.verdict),
      });

      // Le texte se range a GAUCHE du trait sur le dernier quart de la
      // periode : a droite, il sortirait du cadre.
      const ratio = series.findIndex((point) => point.date === x) / Math.max(1, series.length - 1);

      lines.push(
        <ReferenceLine
          key={marker.id}
          x={x}
          stroke={MARKER_LINE.stroke}
          strokeDasharray={MARKER_LINE.strokeDasharray}
          strokeWidth={MARKER_LINE.strokeWidth}
          // Le numero ET la date : le numero apparie le trait a la ligne de la
          // bande, la date dit sur quel jour lire la courbe. Le LIBELLE, lui,
          // reste dans la bande — cinq libelles entiers en haut d'une courbe se
          // chevauchent et ne se lisent plus.
          label={
            <MarkerLineLabel
              text={
                visible.length > MAX_DATED_LABELS
                  ? String(position)
                  : `${position} · ${formatDayShort(marker.date)}`
              }
              color={IMPACT_TONE_COLORS[tone]}
              anchorLeft={ratio > 0.75}
            />
          }
        />,
      );
    }

    return { items, lines };
  }, [rows, markers, value, mode, unit, higherIsBetter, scope]);
}
